<template>
  <div class="tenders-filters-chips" :class="{ 'tenders-filters-chips--stack': stacked }">
    <PlatformChip
      :value="searchFilters.platforms"
      :platforms="platforms"
      :seao-active="seaoActive"
      @change="$emit('platform-change', $event)"
      @settings="$emit('seao-settings')"
    />
    <StatusChip :value="searchFilters.statuses" @change="$emit('status-change', $event)" />
    <ClosingChip :value="searchFilters.closingBefore" @change="$emit('closing-before-change', $event)" />
    <PublicationChip :is-text="labelled" :value="publicationValue" @change="$emit('publication-change', $event)" />

    <div v-if="!labelled" class="tenders-filters-chips__sep"></div>

    <ReadChip :is-text="labelled" :value="searchFilters.readFilters" @change="$emit('read-filter-change', $event)" />
    <FollowedChip
      :is-text="labelled"
      :value="searchFilters.followFilters"
      @change="$emit('follow-filter-change', $event)"
    />
    <SharedChip
      :is-text="labelled"
      :value="searchFilters.shareFilters"
      @change="$emit('share-filter-change', $event)"
    />
    <AssigneesChip
      :value="searchFilters.assigneesFilters"
      :users="users"
      @change="$emit('assignees-filter-change', $event)"
    />
    <ScoreChip
      v-if="scoringEligible"
      :is-text="labelled"
      :value="searchFilters.relevanceScoreRange"
      @change="$emit('relevance-score-filter-change', $event)"
    />

    <div v-if="!labelled" class="tenders-filters-chips__sep"></div>

    <FilterChip
      icon="type"
      :tooltip="t('keywords')"
      :label="labelled ? t('keywords') : null"
      :is-text="labelled"
      :active="keywordsActive"
      :opens-panel="listed"
      @toggle="$emit('open-keywords')"
      @clear="$emit('clear-filter', 'keywords')"
    />
    <CategoriesChip
      :is-text="labelled"
      :value="searchFilters.procurementCategories"
      @change="$emit('categories-change', $event)"
    />
    <FilterChip
      icon="map-pin"
      :tooltip="$t('search-filters.zone.name')"
      :label="stackedZoneLabel"
      :is-text="labelled"
      :badge="labelled ? null : zonesBadge"
      :active="zonesActive"
      :opens-panel="listed"
      @toggle="$emit('zones-settings')"
      @clear="$emit('clear-filter', 'zones')"
    />
    <FilterChip
      icon="archive"
      :tooltip="t('archived')"
      :label="labelled ? t('archived') : null"
      :is-text="labelled"
      :active="archivedActive"
      @toggle="$emit('archived-toggle')"
      @clear="$emit('archived-toggle')"
    />
  </div>
</template>

<script>
import FilterChip from '@/components/filters/FilterChip';
import PlatformChip from '@/components/filters/dimensions/PlatformChip';
import StatusChip from '@/components/filters/dimensions/StatusChip';
import ClosingChip from '@/components/filters/dimensions/ClosingChip';
import PublicationChip from '@/components/filters/dimensions/PublicationChip';
import ReadChip from '@/components/filters/dimensions/ReadChip';
import FollowedChip from '@/components/filters/dimensions/FollowedChip';
import SharedChip from '@/components/filters/dimensions/SharedChip';
import AssigneesChip from '@/components/filters/dimensions/AssigneesChip';
import ScoreChip from '@/components/filters/dimensions/ScoreChip';
import CategoriesChip from '@/components/filters/dimensions/CategoriesChip';

/**
 * Shared filter-chip roster for the tenders inbox — used inline on desktop, stacked inside the
 * compact (tablet) filters dialog, and in the phone filter bar (PhoneFilterBar), which lays the
 * chips out itself and names them.
 */
export default {
  name: 'TendersFiltersChips',
  inject: {
    // Inside the phone filter bar (FilterChipsContext): named, and, in its panel, listed.
    inPhoneBar: { from: 'filterChipsLabelled', default: false },
    inPhonePanel: { from: 'filterChipsListed', default: false },
  },
  components: {
    FilterChip,
    PlatformChip,
    StatusChip,
    ClosingChip,
    PublicationChip,
    ReadChip,
    FollowedChip,
    SharedChip,
    AssigneesChip,
    ScoreChip,
    CategoriesChip,
  },
  props: {
    searchFilters: { type: Object, required: true },
    platforms: { type: Array, default: () => [] },
    // The user roster the assignees chip picks from (the page owns it).
    users: { type: Array, default: () => [] },
    seaoActive: { type: Boolean, default: false },
    scoringEligible: { type: Boolean, default: false },
    keywordsActive: { type: Boolean, default: false },
    zonesActive: { type: Boolean, default: false },
    zonesBadge: { type: String, default: '' },
    archivedActive: { type: Boolean, default: false },
    // Vertical full-width layout for the compact filters dialog.
    stacked: { type: Boolean, default: false },
  },
  computed: {
    publicationValue() {
      return [this.searchFilters.publishedAfter, this.searchFilters.publishedBefore].filter(Boolean);
    },
    // Chips that name what they filter rather than show an icon: stacked, or in the phone bar.
    labelled() {
      return this.stacked || this.inPhoneBar;
    },
    // The phone bar's panel lists the chips, where a line leading to a panel ends with a chevron.
    listed() {
      return this.inPhonePanel;
    },
    stackedZoneLabel() {
      if (!this.labelled) return null;
      const name = this.$t('search-filters.zone.name');
      return this.zonesBadge ? `${name} ${this.zonesBadge}` : name;
    },
  },
  methods: {
    t(key) {
      return this.$t(`page.tenders.views.chips.${key}`);
    },
  },
};
</script>

<style lang="scss">
.tenders-filters-chips {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;

  &__sep {
    width: 1px;
    height: 22px;
    background: $color-border;
    margin: 0 2px;
  }

  &--stack {
    flex-direction: column;
    align-items: stretch;
    flex-wrap: nowrap;
    gap: 10px;

    .filter-chip,
    .platform-chip {
      width: 100%;
    }

    .filter-chip__btn {
      width: 100%;
      justify-content: flex-start;
    }

    .platform-chip .filter-chip {
      width: 100%;
    }
  }
}
</style>
